import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { 
  getTrades, 
  getUserPendingTrades,
  getUserCompletedTrades,
  cancelTrade,
  getTradeById,
  type Trade 
} from '../../services/p2pAPI';
import './TradesManagement.css';
import IconicHeader from '../updated/shared/IconicHeader';
import P2PNavigation from './P2PNavigation';

const TradesManagement: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [activeTab, setActiveTab] = useState('in-progress');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [inProgressCount, setInProgressCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [cancellingTradeId, setCancellingTradeId] = useState<string | null>(null);
  const [cancelledTradeIds, setCancelledTradeIds] = useState<Set<string>>(new Set());
  const [refreshingTradeId, setRefreshingTradeId] = useState<string | null>(null);

  useEffect(() => {
    loadTrades();
    setSuccessMessage(''); // Clear success message when switching tabs
  }, [activeTab]);

  // Apply theme class based on userType (CaptainBee = orange, normal = green)
  // Match the logic used in IconicHeader: if userType !== 'Indexx Exchange', it's CaptainBee
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const userLogged = localStorage.getItem('userlogged');
    
    // Determine theme class based on userType or userlogged
    // Match IconicHeader logic: userType !== 'Indexx Exchange' means CaptainBee
    let themeClass = 'theme-normal'; // Default to green
    
    if (userType && userType !== 'Indexx Exchange') {
      // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'CaptainBee' || userLogged === 'captain') {
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'HoneyUser' || userLogged === 'honeyb') {
      themeClass = 'theme-honeyb'; // Orange for HoneyBee
    } else if (userLogged === 'normal' || userType === 'Indexx Exchange') {
      themeClass = 'theme-normal'; // Green for normal users
    }
    
    // Apply theme class to document body and root
    document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.body.classList.add(themeClass);
    document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.documentElement.classList.add(themeClass);
    
    // Also apply to root element to ensure CSS variables are available
    const root = document.getElementById('root');
    if (root) {
      root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      root.classList.add(themeClass);
    }
    
    // Cleanup on unmount
    return () => {
      // Don't remove theme class on unmount as other components might need it
    };
  }, []);

  const loadTrades = async () => {
    try {
      setLoading(true);
      setError('');

      const userEmail = localStorage.getItem('user');
      let response;
      
      // Use specific API endpoints based on active tab with query parameters
      if (activeTab === 'in-progress') {
        // Fetch pending trades (in progress trades)
        response = await getUserPendingTrades({
          email: userEmail || undefined,
          limit: 20,
          offset: 0
        });
        console.log('Pending trades loaded:', response.data?.length || 0);
      } else {
        // Fetch completed trades
        response = await getUserCompletedTrades({
          email: userEmail || undefined,
          limit: 20,
          offset: 0
        });
        console.log('Completed trades loaded:', response.data?.length || 0);
      }
      
      const trades = response.data || [];
      // Filter out cancelled trades (defensive) and any we've already removed from UI
      const filteredTrades = trades
        .filter(trade => (trade.status || '').toLowerCase() !== 'cancelled')
        .filter(trade => !cancelledTradeIds.has(trade.id));
      setTrades(filteredTrades);

      // Update count for the current tab using filtered trades
      if (activeTab === 'in-progress') {
        setInProgressCount(filteredTrades.length);
      } else {
        setCompletedCount(filteredTrades.length);
      }

      console.log('Trades loaded for', activeTab, 'tab:', filteredTrades.length, '(filtered from', trades.length, 'total)');

    } catch (error: any) {
      console.error('Failed to load trades:', error);
      // Don't set error message if we got an empty array (API might have returned empty)
      if (error?.message && !error.message.includes('Trade not found')) {
      setError(error.message || 'Failed to load trades');
      } else {
        setError('');
      }
      setTrades([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTrade = async (tradeId: string) => {
    if (!window.confirm('Are you sure you want to cancel this trade? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      setCancellingTradeId(tradeId);
      
      console.log('Cancelling trade:', tradeId);
      
      // Call the backend API to cancel the trade with an optional reason
      const response = await cancelTrade(tradeId, 'User cancelled from In Progress list');
      console.log('Cancel trade response:', response);
      
      // Add to cancelled trades set to prevent it from reappearing
      setCancelledTradeIds(prev => {
        const newSet = new Set(prev);
        newSet.add(tradeId);
        console.log(`Added trade ${tradeId} to cancelled set`);
        return newSet;
      });
      
      // Remove the trade from the list immediately after successful cancellation
      setTrades(prevTrades => {
        const updated = prevTrades.filter(trade => trade.id !== tradeId);
        console.log(`Removed trade ${tradeId} from list. Remaining: ${updated.length}`);
        return updated;
      });
      
      // Update the count if we're on the in-progress tab
      if (activeTab === 'in-progress') {
        setInProgressCount(prev => Math.max(0, prev - 1));
      }
      
      console.log('Trade cancelled successfully');
      
      // Clear any error messages
      setError('');
      
    } catch (error: any) {
      console.error('Failed to cancel trade:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        tradeId: tradeId
      });
      
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          error?.response?.data?.error ||
                          'Failed to cancel trade';
      
      setError(errorMessage);
      
      // Handle "Trade not found" error - this might mean trade was already cancelled
      // or doesn't exist, so we'll remove it from UI anyway
      const isNotFoundError = errorMessage.toLowerCase().includes('not found') || 
                              error?.status === 404;
      
      if (isNotFoundError) {
        // Even if backend says "not found", remove it from UI
        // This handles cases where trade was already cancelled or doesn't exist
        console.log('Backend returned "not found" (404) - removing trade from UI');
        
        // Add to cancelled trades set
        setCancelledTradeIds(prev => {
          const newSet = new Set(prev);
          newSet.add(tradeId);
          return newSet;
        });
        
        setTrades(prevTrades => {
          const updated = prevTrades.filter(trade => trade.id !== tradeId);
          console.log(`Removed trade ${tradeId} from list due to not found error. Remaining: ${updated.length}`);
          return updated;
        });
        if (activeTab === 'in-progress') {
          setInProgressCount(prev => Math.max(0, prev - 1));
        }
        // Clear error message since we're handling it gracefully
        setError('');
      } else {
        // For other errors, show alert
        alert(errorMessage);
      }
    } finally {
      setCancellingTradeId(null);
    }
  };

  const handleRefreshTrade = async (tradeId: string) => {
    try {
      setError('');
      setRefreshingTradeId(tradeId);
      
      console.log('Refreshing trade:', tradeId);
      
      // Fetch the latest trade data from the backend
      const response = await getTradeById(tradeId);
      
      if (!response.success || !response.data) {
        // If trade not found, silently remove it from list and reload
        console.log('Trade not found, removing from list');
        setTrades(prevTrades => prevTrades.filter(trade => trade.id !== tradeId));
        if (activeTab === 'in-progress') {
          setInProgressCount(prev => Math.max(0, prev - 1));
        }
        await loadTrades();
        return;
      }
      
      const updatedTrade = response.data;
      console.log('Updated trade data:', updatedTrade);
      
      // If trade is now completed, remove it from current list and reload
      if (updatedTrade.status === 'completed') {
        console.log('Trade is now completed, removing from in-progress list');
        
        // Remove from current trades list
        setTrades(prevTrades => {
          const updated = prevTrades.filter(trade => trade.id !== tradeId);
          return updated;
        });
        
        // Update counts
        if (activeTab === 'in-progress') {
          setInProgressCount(prev => Math.max(0, prev - 1));
          setCompletedCount(prev => prev + 1);
        }
        
        // Show success confirmation message
        setSuccessMessage('Transaction completed successfully! The trade has been approved by admin and moved to Completed tab.');
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
        
        // Reload trades to ensure everything is in sync
        await loadTrades();
      } else {
        // Update the trade in the current list
        setTrades(prevTrades => {
          return prevTrades.map(trade => 
            trade.id === tradeId ? updatedTrade : trade
          );
        });
        
        console.log('Trade status updated:', updatedTrade.status);
      }
      
      // Clear any error messages
      setError('');
      
    } catch (error: any) {
      console.error('Failed to refresh trade:', error);
      
      // Handle "not found" errors gracefully
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          '';
      
      const isNotFoundError = errorMessage.toLowerCase().includes('not found') || 
                              error?.response?.status === 404 ||
                              error?.status === 404;
      
      if (isNotFoundError) {
        // Silently remove trade from list if not found
        console.log('Trade not found (404), removing from list');
        setTrades(prevTrades => prevTrades.filter(trade => trade.id !== tradeId));
        if (activeTab === 'in-progress') {
          setInProgressCount(prev => Math.max(0, prev - 1));
        }
        await loadTrades();
      } else {
        // Only log other errors, don't show to user
        console.error('Error refreshing trade:', errorMessage);
      }
      
      // Don't set error state or show alerts
      setError('');
    } finally {
      setRefreshingTradeId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'secondary', text: 'Pending Payment' },
      paid: { variant: 'info', text: 'Payment Received' },
      crypto_sent: { variant: 'primary', text: 'Crypto Sent' },
      completed: { variant: 'success', text: 'Completed' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      disputed: { variant: 'danger', text: 'Disputed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { 
      variant: 'secondary', 
      text: status 
    };

    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getActionButtons = (trade: Trade) => {
    const buttons = [];
    const currentUser = localStorage.getItem('user');
    const isBuyer = trade.buyerEmail === currentUser;
    const isSeller = trade.sellerEmail === currentUser;

    // Primary Action Button (most important - shown first)
    // Pay Now button removed per user request
    if (trade.status === 'paid' && isSeller) {
      buttons.push(
        <Button 
          key="send-crypto" 
          variant="primary" 
          size="sm" 
          className="btn-primary-action"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/p2p/trades/${trade.id}`);
          }}
        >
          <i className="fas fa-coins"></i>
          Send Crypto
        </Button>
      );
    } else if (trade.status === 'crypto_sent' && isBuyer) {
      buttons.push(
        <Button 
          key="confirm" 
          variant="success" 
          size="sm" 
          className="btn-primary-action"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/p2p/trades/${trade.id}`);
          }}
        >
          <i className="fas fa-check-circle"></i>
          Confirm
        </Button>
      );
    }

    // View Details Button removed per user request

    // Cancel Button removed per user request

    // Dispute Button (disabled)
    if (['pending', 'paid', 'crypto_sent'].includes(trade.status)) {
      buttons.push(
        <Button 
          key="dispute" 
          variant="outline-primary" 
          size="sm"
          className="btn-tertiary-action"
          disabled={true}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/p2p/disputes?tradeId=${trade.id}`);
          }}
        >
          <i className="fas fa-exclamation-triangle"></i>
          Dispute
        </Button>
      );
    }

    // Refresh Button (only in In Progress tab)
    if (activeTab === 'in-progress') {
      const isRefreshing = refreshingTradeId === trade.id;
    buttons.push(
      <Button 
          key="refresh" 
        variant="outline-primary" 
        size="sm" 
          className="btn-tertiary-action"
          onClick={(e) => {
            e.stopPropagation();
            handleRefreshTrade(trade.id);
          }}
          disabled={isRefreshing || !!refreshingTradeId}
        >
          {isRefreshing ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" style={{ width: '10px', height: '10px' }} />
              Refreshing...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt"></i>
              Refresh
            </>
          )}
      </Button>
    );
    }

    return buttons;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to get token logo
  const getTokenLogo = (coinSymbol: string) => {
    try {
      // Normalize the symbol
      const normalizedSymbol = coinSymbol.toUpperCase().trim();
      
      // Handle special cases and variations
      const symbolMap: { [key: string]: string } = {
        'USD': 'USD',
        'USDT': 'USDT',
        'USDC': 'USDC',
        'BTC': 'BTC',
        'ETH': 'ETH',
        'BNB': 'BNB',
        'BUSD': 'BUSD',
        'INEX': 'INEX',
        'IN500': 'IN500',
        'INXC': 'INXC',
        'IUSD': 'IUSD',
        'IUSD+': 'IUSD+',
        'IUSDP': 'IUSDP',
        'ADA': 'ADA',
        'DOT': 'DOT',
        'LINK': 'LINK',
        'MATIC': 'MATIC',
        'SOL': 'SOL',
        'XRP': 'XRP',
        'DOGE': 'DOGE',
        'AVAX': 'AVAX',
        'LTC': 'LTC',
        'CAKE': 'CAKE',
        'AAVE': 'AAVE',
        'SHIB': 'SHIB',
        'TRX': 'TRX',
        'VET': 'VET',
        'CHZ': 'CHZ',
        'MANA': 'MANA',
        'THETA': 'THETA',
        'FTM': 'FTM',
        'ALGO': 'ALCRYP',
        'BCH': 'BCH',
        'XLM': 'XLM',
        'TUSD': 'TUSD',
        'DAI': 'DAI',
        'IBAT': 'IBAT',
        'EPS': 'EPS',
        'LBLOCK': 'LBlock',
      };

      // Get the image name from map or use the symbol directly
      let imageName = symbolMap[normalizedSymbol] || normalizedSymbol;
      
      // Handle special symbols with special characters
      if (normalizedSymbol.includes('+') || normalizedSymbol.includes('-')) {
        imageName = normalizedSymbol.replace(/[+-\s]/g, '');
      }
      
      try {
        // Try loading the image
        return require(`../../assets/token-icons/${imageName}.png`).default;
      } catch (error1) {
        // If that fails, try with the original normalized symbol
        try {
          return require(`../../assets/token-icons/${normalizedSymbol}.png`).default;
        } catch (error2) {
          return null;
        }
      }
    } catch (error) {
      return null;
    }
  };

  const getTradeRole = (trade: Trade) => {
    const currentUser = localStorage.getItem('user');
    if (trade.buyerEmail === currentUser) {
      return { role: 'Buyer', variant: 'secondary' };
    } else if (trade.sellerEmail === currentUser) {
      return { role: 'Seller', variant: 'secondary' };
    }
    return { role: 'Unknown', variant: 'secondary' };
  };

  if (loading) {
    return (
      <div className="trades-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading trades...</p>
      </div>
    );
  }

  return (
    <div className="binance-trades-management">
      <IconicHeader selectedTab="P2P" onChange={() => {}} />
      <P2PNavigation />
      <div className="p2p-content-container">
      {/* Binance-style Header */}
      <div className="binance-header">
        <div className="header-content">
          <h1>My Trades</h1>
        </div>
        <div className="header-actions">
          <Link to="/p2p/offers">
            <Button variant="primary" className="binance-primary-btn">
              <i className="fas fa-search me-2"></i>
              Browse Offers
            </Button>
          </Link>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <Button variant="outline-danger" onClick={loadTrades}>
                <i className="fas fa-sync-alt me-2"></i>
                Retry
              </Button>
            </div>
          </Alert>
        </div>
      )}

      {/* Success Alert */}
      {successMessage && (
        <div className="error-alert">
          <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
            <Alert.Heading>
              <i className="fas fa-check-circle me-2"></i>
              Transaction Completed
            </Alert.Heading>
            <p>{successMessage}</p>
          </Alert>
        </div>
      )}

      {/* Trade Filter Buttons */}
      <div className="trade-filter-section">
        <div className="filter-buttons-container">
          <button
            className={`filter-button ${activeTab === 'in-progress' ? 'active' : ''}`}
            onClick={() => setActiveTab('in-progress')}
          >
            <i className="fas fa-clock me-2"></i>
            In Progress
            {inProgressCount > 0 && (
              <span className="trade-count">{inProgressCount}</span>
            )}
          </button>
          <button
            className={`filter-button ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            <i className="fas fa-check-circle me-2"></i>
            Completed Trades
            {completedCount > 0 && (
              <span className="trade-count">{completedCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Trades Content */}
      {loading ? (
        <div className="trades-loading-state">
          <Spinner animation="border" role="status" />
          <p>Loading trades...</p>
        </div>
      ) : trades.length === 0 ? (
        <div className="empty-state-modern">
                  <div className="empty-state-icon">
            {activeTab === 'in-progress' ? (
              <i className="fas fa-clock"></i>
            ) : (
              <i className="fas fa-check-circle"></i>
            )}
                  </div>
          <h4>
            {activeTab === 'in-progress' 
              ? 'No Trades In Progress' 
              : 'No Completed Trades'}
          </h4>
          <p className="empty-state-text">
            {activeTab === 'in-progress'
                      ? "You don't have any active trades at the moment. Start trading by browsing available offers."
              : "You haven't completed any trades yet. Complete your first trade to see it here."}
                  </p>
                  <div className="empty-state-actions">
                    <Link to="/p2p/offers">
                      <Button variant="primary" className="me-3">
                        <i className="fas fa-search me-2"></i>
                        Browse Offers
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
        <>
          {/* Binance P2P Results Section */}
          <div className="binance-results-section">
            <div className="results-header">
              <div className="results-info">
                <span className="results-type">
                  {activeTab === 'in-progress' ? 'In Progress Trades' : 'Completed Trades'}
                </span>
              </div>
            </div>

            {/* Binance P2P Table Style */}
            <div className="binance-trades-table">
              {/* Table Header */}
              <div className={`trades-table-header ${activeTab === 'completed' ? 'no-actions' : ''}`}>
                <div className="col-offer-id">Offer ID</div>
                <div className="col-trade-id">Trade ID</div>
                <div className="col-crypto">Crypto</div>
                <div className="col-amount">Amount</div>
                <div className="col-price">Price</div>
                <div className="col-total">Total Value</div>
                <div className="col-role">Role</div>
                <div className="col-status">Status</div>
                <div className="col-date">Date</div>
                {activeTab !== 'completed' && <div className="col-action">Actions</div>}
              </div>
            
            {/* Table Body */}
            <div className="trades-table-body">
                      {trades.map((trade) => {
                        const roleInfo = getTradeRole(trade);
                        return (
                  <div 
                    key={trade.id} 
                    className={`trade-table-row ${activeTab === 'completed' ? 'no-actions' : ''}`}
                    onClick={() => window.location.href = `/p2p/trades/${trade.id}`}
                  >
                    {/* Offer ID Column */}
                    <div className="col-offer-id">
                      <div className="offer-id-value">
                        <i className="fas fa-hashtag me-1"></i>
                        <code>{trade.offerId?.slice(0, 10) || 'N/A'}...</code>
                      </div>
                    </div>
                    
                    {/* Trade ID Column */}
                    <div className="col-trade-id">
                      <div className="trade-id-value">
                        <i className="fas fa-hashtag me-1"></i>
                        <code>{trade.id.slice(0, 10)}...</code>
                      </div>
                    </div>
                    
                    {/* Crypto Column */}
                    <div className="col-crypto">
                      {(() => {
                        const tokenLogo = getTokenLogo(trade.cryptoType);
                        return (
                          tokenLogo ? (
                            <img 
                              src={tokenLogo} 
                              alt={trade.cryptoType}
                              className="crypto-icon-image"
                              style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                            />
                          ) : (
                              <Badge bg="info">{trade.cryptoType}</Badge>
                          )
                        );
                      })()}
                    </div>
                    
                    {/* Amount Column */}
                    <div className="col-amount">
                      <div className="trade-amount-value">
                        {trade.amount} {trade.cryptoType}
                      </div>
                              </div>
                    
                    {/* Price Column */}
                    <div className="col-price">
                      <div className="trade-price-value">
                        {formatCurrency(trade.price)}
                </div>
            </div>
                    
                    {/* Total Value Column */}
                    <div className="col-total">
                      <div className="trade-total-row-value">
                        {formatCurrency(trade.amount * trade.price)}
                      </div>
                </div>
                    
                    {/* Role Column */}
                    <div className="col-role">
                              <Badge bg={roleInfo.variant}>{roleInfo.role}</Badge>
                    </div>
                    
                    {/* Status Column */}
                    <div className="col-status">
                      {getStatusBadge(trade.status)}
                    </div>
                    
                    {/* Date Column */}
                    <div className="col-date">
                      <div className="trade-date-value">
                        {formatDate(activeTab === 'completed' ? trade.updatedAt : trade.createdAt)}
                      </div>
                    </div>
                    
                    {/* Actions Column */}
                    {activeTab !== 'completed' && (
                      <div className="col-action" onClick={(e) => e.stopPropagation()}>
                        <div className="trade-action-buttons">
                                {getActionButtons(trade)}
                              </div>
                      </div>
                    )}
                  </div>
                        );
                      })}
            </div>
          </div>
        </div>
        </>
      )}
      </div>
    </div>
  );
};

export default TradesManagement;
